/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { SimpleList, Button, Checkbox, TextInput } from 'carbon-addons-iot-react';
import { Edit16 } from '@carbon/icons-react';
import sortBy from 'lodash/sortBy';
import hash from 'object-hash';

import { BaseModal } from 'Common';

const messages = defineMessages({
  editColumn: 'Edit column',
  save: 'Save',
  cancel: 'Cancel',
  defaultHeader: 'Default header: {name}',
  columnHeader: 'Column header',
  editButtonTitle: 'Edit {item}',
  dragHandle: 'Drag handle',
});

const propTypes = {
  onChange: PropTypes.func.isRequired,
  cardConfig: PropTypes.shape({
    id: PropTypes.string,
    columnsState: PropTypes.arrayOf(PropTypes.object),
  }),
};

const defaultProps = {
  cardConfig: {},
};

/** Sort the columns such that the visible ones are at the top of the list,
 * and the invisible ones are at the bottom in alphabetical order */
export const sortColumns = columns => {
  const visibleColumns = columns.filter(({ isVisible }) => isVisible);
  const sortedInvisibleColumns = sortBy(
    columns.filter(({ isVisible }) => !isVisible),
    ({ columnName }) => columnName.toLowerCase() // Sort by the column name
  );
  return [...visibleColumns, ...sortedInvisibleColumns];
};

const AlertCardEditContent = ({ onChange, cardConfig }) => {
  const { formatMessage } = useIntl();
  const { columnsState } = cardConfig;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editColumnName, setEditColumnName] = useState({});
  const hasUserReorder = useRef(false); // Keep track of whether the user has reordered the columns

  const columnsToShow = columnsState;

  const listI18n = useMemo(
    () => ({ dragHandle: formatMessage(messages.dragHandle) }),
    [formatMessage]
  );

  return (
    <div className="alert-card-edit-content">
      <SimpleList
        key={`${cardConfig.id}-content-list--${hash(columnsState)}`}
        hasPagination={false}
        editingStyle="single"
        i18n={listI18n}
        onListUpdated={(
          newColumnOrder // Event for drag and drop in the content editor. isVisible is handled by the checkbox in the items
        ) => {
          hasUserReorder.current = true;
          onChange({
            ...cardConfig,
            columnsState: newColumnOrder.map(({ id }) =>
              columnsToShow.find(({ id: columnId }) => columnId === id)
            ),
          });
        }}
        items={columnsToShow.map(({ id, columnName, isVisible }) => ({
          id,
          content: {
            value: columnName || id,
            icon: (
              <Checkbox
                key={`${columnName}-selector`}
                id={`${columnName}-selector`}
                data-testid={`${columnName}-checkbox-test-id`}
                hideLabel
                checked={isVisible}
                onChange={checked => {
                  const updatedColumns = [...columnsToShow];
                  const columnIndex = updatedColumns.findIndex(
                    column => column.columnName === columnName
                  );
                  updatedColumns[columnIndex].isVisible = checked;
                  onChange({
                    ...cardConfig,
                    columnsState: updatedColumns,
                  });
                }}
              />
            ),
            rowActions: () => [
              <Button
                key={`${columnName}-edit`}
                title={formatMessage(messages.editButtonTitle, {
                  item: columnName,
                })}
                kind="ghost"
                renderIcon={Edit16}
                hasIconOnly
                onClick={() => {
                  setEditColumnName({ id, oldColumnName: columnName, newColumnName: '' });
                  setIsEditModalOpen(true);
                }}
              />,
            ],
          },
          isSelectable: true,
        }))}
      />
      <BaseModal
        key={`edit-column--${editColumnName.id}`}
        size="xs"
        isOpen={isEditModalOpen}
        header={{ title: formatMessage(messages.editColumn), label: editColumnName.oldColumnName }}
        footer={{
          primaryButtonText: formatMessage(messages.save),
          secondaryButtonText: formatMessage(messages.cancel),
        }}
        onSubmit={() => {
          const { id, oldColumnName, newColumnName } = editColumnName;
          const updatedColumns = [...columnsToShow];
          const columnIndex = updatedColumns.findIndex(column => column.id === id);
          updatedColumns[columnIndex].columnName = newColumnName || oldColumnName;
          onChange({ ...cardConfig, columnsState: updatedColumns });
          setEditColumnName({});
          setIsEditModalOpen(false);
        }}
        onClose={() => {
          setEditColumnName({});
          setIsEditModalOpen(false);
        }}>
        <TextInput
          helperText={formatMessage(messages.defaultHeader, { name: editColumnName.id })}
          labelText={formatMessage(messages.columnHeader)}
          onChange={e => setEditColumnName({ ...editColumnName, newColumnName: e.target.value })}
          defaultValue={editColumnName.oldColumnName}
        />
      </BaseModal>
    </div>
  );
};

AlertCardEditContent.propTypes = propTypes;
AlertCardEditContent.defaultProps = defaultProps;

export default AlertCardEditContent;
